import { ContentComponentType, InternalContentComponentType } from '@ppg/core/enums';
import { InlineImageComponent, InlineImageWrapperComponent, RichTextModel } from '@ppg/core/models';

function isComponentType<T>(type: string): IsComponentType<T> {
  return ((value: unknown) =>
    !!value && typeof value === 'object' && 'type' in value && value.type === type) as IsComponentType<T>;
}

type IsComponentType<T> = (value: unknown) => value is T;

const isRichTextModel = isComponentType<RichTextModel>(ContentComponentType.RICH_TEXT);
const isInlineImageComponent = isComponentType<InlineImageComponent>(ContentComponentType.INLINE_IMAGE);
const isInlineImageWrapperComponent = isComponentType<InlineImageWrapperComponent>(
  InternalContentComponentType.INLINE_IMAGE_WRAPPER_COMPONENT,
);

const isRichTextEmptyNewLine = (value: unknown): value is RichTextModel =>
  isRichTextModel(value) && value.content === '<p><br></p>';

export function applyRichTextFixes<T extends RichTextModel | InlineImageComponent | unknown>(
  items: T[] | null | undefined,
): T[] | null {
  if (!items || items.length === 0) {
    return null;
  }

  const parsedItems = items.reduce((acc, item, i) => {
    const prevItem = acc[acc.length - 1];

    if (isInlineImageComponent(item)) {
      const inlineImageWrapper: InlineImageWrapperComponent = {
        type: InternalContentComponentType.INLINE_IMAGE_WRAPPER_COMPONENT,
        content: { image: item, texts: [] },
      };

      acc.push(inlineImageWrapper as T);

      return acc;
    }

    if (isInlineImageWrapperComponent(prevItem) && isRichTextModel(item)) {
      const updatedInlineImageWrapper: InlineImageWrapperComponent = {
        ...prevItem,
        content: {
          ...prevItem.content,
          texts: [...prevItem.content.texts, item],
        },
      };

      acc[acc.length - 1] = updatedInlineImageWrapper as T;

      return acc;
    }

    if (items.length - 1 === i && isRichTextEmptyNewLine(item)) {
      // remove empty new line at the end of rich text
      return acc;
    }

    if (isRichTextModel(prevItem) && isRichTextModel(item)) {
      acc[acc.length - 1] = {
        ...prevItem,
        content: prevItem.content + item.content,
      };

      return acc;
    }

    if (isRichTextEmptyNewLine(item)) {
      // remove empty new line from the beginning of rich text
      return acc;
    }

    acc.push(item);

    return acc;
  }, [] as T[]);

  if (parsedItems.length === 0) {
    return null;
  }

  return parsedItems;
}
